import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading } from "@/components/States";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const patch = async (id, body) => {
    try { await api.patch(`/admin/users/${id}`, body); toast.success("User updated"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const [expiry, setExpiry] = useState({});
  const setPlan = async (u, plan_id) => {
    try { await api.put(`/admin/users/${u.id}/plan`, { plan_id, expires_at: expiry[u.id] || null }); toast.success(`${u.email} moved to ${plan_id}`); load(); } catch (e) { toast.error(formatError(e)); }
  };
  if (!users) return <Loading />;
  return (
    <div data-testid="admin-users" className="overflow-x-auto rounded-xl border border-line bg-surface">
      <table className="w-full text-sm">
        <thead className="bg-canvas-2 text-left text-xs uppercase tracking-wider text-ink-3">
          <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3">Plan</th><th className="p-3">Actions</th></tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} data-testid={`admin-user-row-${u.email}`} className="border-t border-line">
              <td className="p-3 font-medium">{u.name}</td><td className="p-3">{u.email}</td>
              <td className="p-3"><Badge variant={u.role === "admin" ? "default" : "secondary"}>{u.role}</Badge></td>
              <td className="p-3">{u.active === false ? <span className="text-red-600">Inactive</span> : <span className="text-emerald-700">Active</span>}</td>
              <td className="p-3">
                {u.role === "learner" ? (
                  <div className="flex items-center gap-2">
                    <Select value={u.plan} onValueChange={(v) => setPlan(u, v)}>
                      <SelectTrigger data-testid={`admin-user-plan-${u.email}`} className="h-8 w-24"><SelectValue /></SelectTrigger>
                      <SelectContent>{["free", "plus", "pro"].map((p) => <SelectItem key={p} value={p} data-testid={`admin-user-plan-option-${p}`} className="capitalize">{p}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="w-44"><DatePicker testId={`admin-user-plan-expiry-${u.email}`} placeholder="No expiry" fromToday value={expiry[u.id] || ""} onChange={(v) => setExpiry({ ...expiry, [u.id]: v })} /></div>
                    {u.plan_source === "grandfathered" && <span className="text-[10px] text-ink-3">early</span>}
                    {u.plan_expires_at && <span className="text-[10px] text-ink-3">until {new Date(u.plan_expires_at).toLocaleDateString()}</span>}
                  </div>
                ) : <span className="text-xs text-ink-3">n/a</span>}
              </td>
              <td className="p-3">
                {u.id !== me.id && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" data-testid={`admin-user-role-${u.email}`} onClick={() => patch(u.id, { role: u.role === "admin" ? "learner" : "admin" })}>{u.role === "admin" ? "Make learner" : "Make admin"}</Button>
                    <Button size="sm" variant="ghost" data-testid={`admin-user-active-${u.email}`} onClick={() => patch(u.id, { active: u.active === false })}>{u.active === false ? "Activate" : "Deactivate"}</Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
